import { useEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import {
  CalendarDays,
  ChevronGrabberVertical,
  ChevronGrabberVerticalThin,
  ChevronLeft,
  DotsVertical,
  PencilLine,
  Trash,
} from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { Todo, User } from "@acto/taskpane/core/domain";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { TodoStatus } from "@acto/gql/graphql";
import { AssigneePickerSheet } from "@acto/taskpane/field-sales-2/components/patterns/AssigneePickerSheet";
import { useAssigneeToggle } from "@acto/taskpane/field-sales-2/data-access/todos/useAssigneeToggle";
import { BottomSheet, SheetHeaderButton } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { DatePicker } from "@acto/taskpane/field-sales-2/components/primitives/DatePicker";
import { toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { ExpandableAccordion } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet/ui/ExpandableAccordion";

const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]}`,
}));

const Card = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

const PillCard = styled(Card)({
  gap: 0,
  padding: 0,
});

const DescriptionRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": {
    flexShrink: 0,
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
    marginTop: theme.fieldSalesTwo.spacing[2],
  },
}));

const DescriptionInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: "5.5rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
  "&:disabled": { color: theme.fieldSalesTwo.colors.neutral[900], WebkitTextFillColor: "currentColor" },
}));

const SelectRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  border: "none",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: "transparent",
  transition: "background 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
  ...pressInteraction(theme, { scale: 1 }),
  '&[aria-expanded="true"]': {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  '&[aria-disabled="true"]': { cursor: "default" },
  // Disabled means "not clickable" — the chevron is dropped at the call site, but the label stays
  // full-contrast rather than greying out.
  "&:disabled": { pointerEvents: "none", cursor: "default" },
}));

const SelectLeft = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

const SelectValue = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[900],
  '&[data-placeholder="true"]': {
    color: theme.fieldSalesTwo.colors.neutral[700],
    fontWeight: theme.fieldSalesTwo.font.weight[500],
  },
  "& svg": { width: theme.fieldSalesTwo.spacing[18], height: theme.fieldSalesTwo.spacing[18], flexShrink: 0 },
}));

const SelectValueText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const HelperText = styled("p")(({ theme }) => ({
  margin: 0,
  // Extra top margin on top of the Fields gap widens the space below the assignee row specifically
  // (matching the design's wider gap there vs. the uniform gap between the other rows).
  marginTop: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const ReadOnlyDescriptionCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.fieldSalesTwo.radius[32],
  borderColor: theme.fieldSalesTwo.colors.surface.glassBorder,
}));

const SubmitButton = styled(SolidCtaButton)({
  minHeight: "3.5rem",
});

/** Local-midnight (domain `Todo.dueDate`) → UTC-midnight (DatePicker), preserving the calendar day. */
function toPickerDate(local: Date): Date {
  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
}

function sameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return a === b;
  return toIsoDate(a) === toIsoDate(b);
}

interface TodoEditSheetProps {
  /** The todo to edit, or null when the sheet is closed. */
  todo: Todo | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reassignment routes here so the caller's optimistic hook can apply the 3s grace period. */
  onReassign: (todo: Todo, newAssignee: User) => void;
  /** Fired after a content edit or delete has been committed (sheet already closing). */
  onSaved?: () => void;
}

/**
 * Edit surface for a todo, opened from a list row. The field set is driven by the todo kind:
 * manual/note todos edit description + due date (with an inline date picker) and can be deleted;
 * workflow todos expose only the assignee (description/due date are read-only, no delete).
 * Completed/deleted todos never reach this sheet — the row-tap call sites guard that.
 */
export function TodoEditSheet({ todo, isOpen, onOpenChange, onReassign, onSaved }: TodoEditSheetProps) {
  const t = useTranslations("fieldSalesTwo.todoEdit");
  const { locale } = useLocale();
  const { updateTodos } = useUpdateTodos();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Manual (neither workflow nor note) and note-based todos are fully editable; workflow todos
  // expose only the assignee (their description/due date are workflow-owned).
  const contentEditable = todo != null && !todo.isWorkflow;
  const initialDueDate = todo?.dueDate ? toPickerDate(todo.dueDate) : null;

  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [assignee, setAssignee] = useState<User | null>(null);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const assigneeRowRef = useRef<HTMLButtonElement>(null);

  const { selectedId: selectedAssigneeId, disabled: assigneeDisabled } = useAssigneeToggle(todo?.client?.id, assignee);

  // The sheet instance is reused across rows (the Radix portal keeps this component mounted), so the
  // draft is (re)seeded whenever the target todo changes rather than via per-mount useState defaults.
  useEffect(() => {
    if (!todo) return;
    setDescription(todo.description ?? todo.title ?? "");
    setDueDate(todo.dueDate ? toPickerDate(todo.dueDate) : null);
    setAssignee(todo.assignee ?? null);
    setCalendarExpanded(false);
    setAssigneePickerOpen(false);
  }, [todo]);

  const dueDateLabel = dueDate ? formatDayMonthYear(dueDate, locale) : t("dueDateLabel");

  const descriptionChanged = contentEditable && description.trim() !== (todo?.description ?? todo?.title ?? "");
  const dueDateChanged = contentEditable && !sameDay(dueDate, initialDueDate);
  const assigneeChanged = todo != null && (assignee?.id ?? null) !== (todo.assignee?.id ?? null);
  const hasChanges = descriptionChanged || dueDateChanged || assigneeChanged;

  // Description is required (for editable todos); Save also needs at least one actual change so it
  // stays disabled on an untouched todo.
  const canSave = todo != null && (!contentEditable || description.trim().length > 0) && hasChanges;

  const handleSave = async () => {
    if (!todo || !canSave) return;

    // Content edits save immediately (matching recap); reassignment routes through the caller's
    // grace-period hook so the row keeps the 3s undo on the list surfaces.
    if (descriptionChanged || dueDateChanged) {
      await updateTodos({
        input: {
          id: todo.id,
          processedStatus: TodoStatus.Open,
          description: descriptionChanged ? description.trim() : undefined,
          dueDate: dueDateChanged ? (dueDate ? toIsoDate(dueDate) : null) : undefined,
        },
      });
    }

    if (assigneeChanged && assignee) {
      onReassign(todo, assignee);
    }

    onOpenChange(false);
    onSaved?.();
  };

  const handleDelete = async () => {
    if (!todo) return;
    await updateTodos({ input: { id: todo.id, processedStatus: TodoStatus.Deleted } });
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      variant="fullScreen"
      title={t("title")}
      closeAriaLabel={t("closeAriaLabel")}
      closeIcon={<ChevronLeft />}
      bodyRef={bodyRef}
      headerAction={
        contentEditable ? (
          <MenuPopover
            trigger={
              <SheetHeaderButton type="button" aria-label={t("delete")}>
                <DotsVertical />
              </SheetHeaderButton>
            }
            ariaLabel={t("delete")}
            items={[{ label: t("delete"), icon: <Trash aria-hidden="true" />, onSelect: () => void handleDelete() }]}
          />
        ) : undefined
      }
    >
      {todo && (
        <>
          <Fields>
            {contentEditable ? (
              <>
                <Card>
                  <DescriptionRow>
                    <PencilLine aria-hidden="true" />
                    <DescriptionInput
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder={t("descriptionPlaceholder")}
                      aria-label={t("descriptionLabel")}
                    />
                  </DescriptionRow>
                </Card>

                <PillCard>
                  <ExpandableAccordion calendarExpanded={calendarExpanded} scrollRef={bodyRef}>
                    <DatePicker
                      value={dueDate}
                      onChange={(date) => {
                        setDueDate(date);
                        setCalendarExpanded(false);
                      }}
                    />
                    <Divider />
                  </ExpandableAccordion>
                  <SelectRow
                    type="button"
                    aria-expanded={calendarExpanded}
                    onClick={() => setCalendarExpanded((expanded) => !expanded)}
                  >
                    <SelectLeft>
                      <CalendarDays aria-hidden="true" />
                      {t("dueDateLabel")}
                    </SelectLeft>
                    <SelectValue>
                      <SelectValueText>{dueDateLabel}</SelectValueText>
                      <ChevronGrabberVertical aria-hidden="true" />
                    </SelectValue>
                  </SelectRow>
                </PillCard>

                <PillCard>
                  <SelectRow
                    ref={assigneeRowRef}
                    type="button"
                    onClick={() => setAssigneePickerOpen(true)}
                    disabled={assigneeDisabled}
                  >
                    <SelectLeft>{t("assigneeLabel")}</SelectLeft>
                    <SelectValue data-placeholder={!assignee}>
                      <SelectValueText>{assignee?.displayName ?? t("assigneePlaceholder")}</SelectValueText>
                      {!assigneeDisabled && <ChevronGrabberVerticalThin aria-hidden="true" />}
                    </SelectValue>
                  </SelectRow>
                </PillCard>
              </>
            ) : (
              // Workflow todos: assignee is the only editable field, surfaced first, followed by an
              // explanatory note and the workflow-owned fields (description, client, due date) as
              // read-only rows.
              <>
                <PillCard>
                  <SelectRow
                    ref={assigneeRowRef}
                    type="button"
                    onClick={() => setAssigneePickerOpen(true)}
                    disabled={assigneeDisabled}
                  >
                    <SelectLeft>{t("assigneeLabel")}</SelectLeft>
                    <SelectValue data-placeholder={!assignee}>
                      <SelectValueText>{assignee?.displayName ?? t("assigneePlaceholder")}</SelectValueText>
                      {!assigneeDisabled && <ChevronGrabberVerticalThin aria-hidden="true" />}
                    </SelectValue>
                  </SelectRow>
                </PillCard>

                <HelperText>{t("workflowHelperText")}</HelperText>

                <ReadOnlyDescriptionCard>
                  <DescriptionRow>
                    <DescriptionInput value={description} aria-label={t("descriptionLabel")} disabled readOnly />
                  </DescriptionRow>
                </ReadOnlyDescriptionCard>

                {todo.client && (
                  <PillCard>
                    <SelectRow as="div" aria-disabled="true">
                      <SelectLeft>{t("customerLabel")}</SelectLeft>
                      <SelectValue>
                        <SelectValueText>{todo.client.name}</SelectValueText>
                      </SelectValue>
                    </SelectRow>
                  </PillCard>
                )}

                <PillCard>
                  <SelectRow as="div" aria-disabled="true">
                    <SelectLeft>{t("dueDateLabel")}</SelectLeft>
                    <SelectValue>
                      <SelectValueText>{dueDateLabel}</SelectValueText>
                    </SelectValue>
                  </SelectRow>
                </PillCard>
              </>
            )}
          </Fields>

          <CtaRow>
            <SubmitButton type="button" onClick={handleSave} disabled={!canSave}>
              {t("save")}
            </SubmitButton>
          </CtaRow>

          <AssigneePickerSheet
            isOpen={assigneePickerOpen}
            onOpenChange={setAssigneePickerOpen}
            anchorRef={assigneeRowRef}
            clientId={todo.client?.id}
            selectedId={selectedAssigneeId}
            onSelect={(picked: User) => setAssignee(picked)}
          />
        </>
      )}
    </BottomSheet>
  );
}
